import { useActivityFlowContext } from "@context/ActivityFlowContext";
import type { Role } from "@app-types/activity-data.types";
import { levelDetails, roleDetails, stepContent, toolDetails } from "@data";
import {
  CompletedSelectionMessage,
  NavigationButton,
  OptionButton,
  PreferenceStep,
  TaskOptionButton,
} from "@components";
import { useState } from "react";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { isValid } from "@utils/isValid";
import {
  isLevelCompleteForRoleTool,
  isToolCompleteForRole,
} from "@utils/helperFunctions";
import { theme } from "@theme";

enum nextLabels {
  "Select Tools",
  "Skill Level",
  "Pick a Task",
  "Select Task",
}

export const PreferencesView = () => {
  const [validationAttemptedStep, setValidationAttemptedStep] =
    useState<number>();
  const {
    currentStep,
    roles,
    tools,
    levels,
    availableActivities: activities,
    completedActivityIds,
    selectedRole,
    selectedTool,
    selectedLevel,
    selectedActivity,
    selectRole: onSelectRole,
    selectTool: onSelectTool,
    selectLevel: onSelectLevel,
    selectActivity: onSelectActivity,
    continuePreferences: onContinue,
    goBack: onBack,
    startPreferences: onRestart,
  } = useActivityFlowContext();

  const content = stepContent[currentStep - 1];
  const nextLabel = nextLabels[currentStep - 1];
  const levelDetailsLabel = levelDetails[selectedLevel || "beginner"].label;

  const orderedRoles = (Object.keys(roleDetails) as Role[]).filter((role) =>
    roles.includes(role),
  );
  const selectedToolComplete =
    selectedRole && selectedTool
      ? isToolCompleteForRole(selectedRole, selectedTool, completedActivityIds)
      : false;
  const selectionComplete =
    (currentStep === 3 && selectedToolComplete) ||
    (currentStep === 4 && activities.length === 0);

  const stepDescription =
    currentStep === 4 && selectedRole && selectedTool && selectedLevel
      ? `We found ${activities.length} ${activities.length === 1 ? "scenario" : "scenarios"} on ${toolDetails[selectedTool].label.split(" - ")[0]} matching your ${levelDetailsLabel} ${roleDetails[selectedRole].label.replace(" Designer", "")} profile.`
      : content?.description;

  const isValidated = isValid({
    currentStep,
    selectedRole,
    selectedTool,
    selectedLevel,
    selectedActivity,
  });
  const showValidationError =
    validationAttemptedStep === currentStep &&
    !isValidated &&
    !selectionComplete;

  const handleContinue = () => {
    if (isValidated) {
      onContinue();
      return;
    }

    setValidationAttemptedStep(currentStep);
  };

  return (
    <Box
      sx={{
        padding: 4,
      }}
    >
      <Typography
        sx={{
          color: theme.palette.primary.main,
          fontSize: "0.75rem",
          fontWeight: 800,
          textTransform: "uppercase",
        }}
      >
        Step {currentStep} of 4
      </Typography>
      <Typography
        variant="h1"
        sx={{
          margin: "8px 0 4px",
        }}
      >
        {content?.title}
      </Typography>
      <Typography>{stepDescription}</Typography>

      {currentStep === 1 && (
        <PreferenceStep>
          {orderedRoles.map((role) => {
            const singleRole = roleDetails[role];

            return (
              <OptionButton
                key={role}
                label={singleRole.label}
                description={singleRole.description}
                icon={singleRole.icon}
                selected={selectedRole === role}
                onClick={() => onSelectRole(role)}
              />
            );
          })}
        </PreferenceStep>
      )}
      {currentStep === 2 && (
        <PreferenceStep layout="list">
          {tools.map((tool) => {
            const singleTool = toolDetails[tool];
            const complete = selectedRole
              ? isToolCompleteForRole(selectedRole, tool, completedActivityIds)
              : false;

            return (
              <OptionButton
                key={tool}
                label={singleTool.label}
                description={singleTool.description}
                icon={
                  <Typography
                    sx={{
                      backgroundColor: singleTool.colour,
                      display: "grid",
                      minWidth: 43,
                      height: 43,
                      borderRadius: 2,
                      placeItems: "center",
                      color: "white",
                      fontSize: "1.1rem",
                      fontWeight: 800,
                    }}
                  >
                    {singleTool.letter}
                  </Typography>
                }
                variant="horizontal"
                complete={complete}
                selected={selectedTool === tool}
                onClick={() => onSelectTool(tool)}
              />
            );
          })}
        </PreferenceStep>
      )}
      {currentStep === 3 && (
        <PreferenceStep layout={selectedToolComplete ? "list" : "grid"}>
          {selectedToolComplete ? (
            <CompletedSelectionMessage onRestart={onRestart} />
          ) : (
            levels.map((level) => {
              const singleLevel = levelDetails[level];
              const complete =
                selectedRole && selectedTool
                  ? isLevelCompleteForRoleTool(
                      selectedRole,
                      selectedTool,
                      level,
                      completedActivityIds,
                    )
                  : false;

              return (
                <OptionButton
                  key={level}
                  label={singleLevel.label}
                  description={singleLevel.description}
                  variant="level"
                  complete={complete}
                  selected={selectedLevel === level}
                  onClick={() => onSelectLevel(level)}
                />
              );
            })
          )}
        </PreferenceStep>
      )}
      {currentStep === 4 && (
        <PreferenceStep layout="list">
          {activities.map((activity) => (
            <TaskOptionButton
              key={activity.id}
              activity={activity}
              levelDetailsLabel={levelDetailsLabel}
              selected={selectedActivity?.id === activity.id}
              onClick={() => onSelectActivity(activity)}
            />
          ))}
          {activities.length === 0 && (
            <CompletedSelectionMessage onRestart={onRestart} />
          )}
        </PreferenceStep>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          justifyContent: currentStep !== 1 ? "space-between" : "flex-end",
          marginTop: 3,
        }}
      >
        {currentStep > 1 && (
          <NavigationButton
            variant="outlined"
            startIcon={<ArrowBack />}
            type="button"
            onClick={onBack}
          >
            Back
          </NavigationButton>
        )}
        {!selectionComplete && (
          <NavigationButton
            type="button"
            inactive={!isValidated}
            endIcon={currentStep < 4 ? <ArrowForward /> : undefined}
            aria-describedby={
              showValidationError ? "preference-error" : undefined
            }
            onClick={handleContinue}
          >
            {currentStep < 4 ? `Next: ${nextLabel}` : nextLabel}
          </NavigationButton>
        )}
      </Box>
      {showValidationError && (
        <Typography
          sx={{
            margin: "10px 4px 0",
            color: theme.palette.primary.dark,
            fontSize: "0.875rem",
            textAlign: "right",
          }}
          id="preference-error"
          role="alert"
        >
          Select an option to continue
        </Typography>
      )}
    </Box>
  );
};
